import { useCallback, useEffect, useState } from 'react'
import { userClient } from '../lib/userClient'
import { useUserAuthStore } from '../store/userAuthStore'
import type { WaybookTrip } from '../types'

interface CreateTripPayload {
  title: string
  description?: string | null
  status?: 'planning' | 'active' | 'completed' | 'archived'
  privacy?: 'public' | 'unlisted' | 'private'
  type?: 'live' | 'shared' | 'personal'
  start_date?: string | null
  end_date?: string | null
}

interface UpdateTripPayload extends Partial<CreateTripPayload> {
  cover_image_url?: string | null
}

export function useUserTrips(options: { autoFetch?: boolean; limit?: number } = {}) {
  const { autoFetch = true, limit = 20 } = options
  const user = useUserAuthStore((state) => state.user)
  const userId = user?.id

  const [trips, setTrips] = useState<WaybookTrip[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTrips = useCallback(async () => {
    if (!userId) {
      setTrips([])
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await userClient.getTrips({ created_by: userId, limit })
      const payload = response.data?.data as WaybookTrip[] | undefined
      const filtered = (payload ?? []).filter((trip) => {
        if (!userId) return true
        const creatorId = (trip as any)?.creator?.id
        return !creatorId || creatorId === userId
      })
      setTrips(filtered)
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load trips'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [userId, limit])

  const createTrip = useCallback(
    async (payload: CreateTripPayload) => {
      try {
        const response = await userClient.createTrip(payload)
        const createdTrip = response.data?.data as WaybookTrip | undefined
        if (createdTrip) {
          setTrips((prev) => [createdTrip, ...prev])
        }
        return createdTrip
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to create trip'
        setError(message)
        throw err
      }
    },
    []
  )

  const updateTrip = useCallback(
    async (id: string, payload: UpdateTripPayload) => {
      try {
        const response = await userClient.updateTrip(id, payload)
        const updatedTrip = response.data?.data as WaybookTrip | undefined
        if (updatedTrip) {
          setTrips((prev) => prev.map((trip) => (trip.id === id ? updatedTrip : trip)))
        }
        return updatedTrip
      } catch (err: any) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to update trip'
        setError(message)
        throw err
      }
    },
    []
  )

  const deleteTrip = useCallback(async (id: string) => {
    try {
      await userClient.deleteTrip(id)
      setTrips((prev) => prev.filter((trip) => trip.id !== id))
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete trip'
      setError(message)
      throw err
    }
  }, [])

  useEffect(() => {
    if (autoFetch) {
      fetchTrips()
    }
  }, [autoFetch, fetchTrips])

  return {
    trips,
    loading,
    error,
    fetchTrips,
    createTrip,
    updateTrip,
    deleteTrip,
  }
}
